// Plumbing pricing calculation
import { 
  PLUMBING_SERVICE_PRICES, 
  PLUMBING_QUANTITY_MULTIPLIERS,
  ACCESS_DIFFICULTY_MODIFIERS,
  LEAK_SEVERITY_MODIFIERS
} from './plumbingData';
import { sumPriceRanges, applyMultiplier } from '../../pricing/pricingEngine';

/**
 * Calculate plumbing estimate based on selected services and answers
 * @param {Array<string>} selectedServices - Array of selected service values
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number, hasValidPrice: boolean}}
 */
export const calculatePlumbingEstimate = (selectedServices, answers) => {
  if (!selectedServices || selectedServices.length === 0) {
    return { low: 0, high: 0, hasValidPrice: false };
  }
  
  let totalLow = 0;
  let totalHigh = 0;
  
  // Services that can use the quantity multiplier
  const quantifiableServices = [
    'faucet_install', 'faucet_repair', 'faucet_replace', 
    'showerhead_install', 'toilet_install', 'toilet_replace', 
    'toilet_repair', 'sink_install', 'sink_replace'
  ];
  
  // Services affected by leak severity
  const leakServices = ['leak_repair', 'pipe_repair', 'water_line_repair'];
  
  // Group services by pricing type
  const quantifiableSelected = [];
  const leakSelected = [];
  const regularSelected = [];
  
  selectedServices.forEach(serviceValue => {
    if (quantifiableServices.includes(serviceValue)) {
      quantifiableSelected.push(serviceValue);
    } else if (leakServices.includes(serviceValue)) {
      leakSelected.push(serviceValue);
    } else {
      regularSelected.push(serviceValue);
    }
  });
  
  // Calculate quantifiable services (fixtures) with quantity multiplier
  if (quantifiableSelected.length > 0) {
    const fixtureQuantity = answers.fixture_quantity || "1";
    const multiplier = PLUMBING_QUANTITY_MULTIPLIERS[fixtureQuantity] || 1;
    
    // Average price of selected quantifiable services
    const avgPrices = quantifiableSelected.map(svc => PLUMBING_SERVICE_PRICES[svc] || { low: 0, high: 0 });
    const basePriceSum = sumPriceRanges(avgPrices);
    
    // Apply multiplier to combined base price (not individual services)
    const avgBaseLow = basePriceSum.low / quantifiableSelected.length;
    const avgBaseHigh = basePriceSum.high / quantifiableSelected.length;
    
    totalLow += Math.round(avgBaseLow * multiplier * quantifiableSelected.length * 0.7); // Volume discount
    totalHigh += Math.round(avgBaseHigh * multiplier);
  }
  
  // Calculate leak services with severity modifier
  if (leakSelected.length > 0) {
    const leakSeverity = answers.leak_severity || "dripping";
    const severityModifier = LEAK_SEVERITY_MODIFIERS[leakSeverity] || { low: 0, high: 0 };
    
    leakSelected.forEach(serviceValue => {
      const basePrice = PLUMBING_SERVICE_PRICES[serviceValue] || { low: 0, high: 0 };
      totalLow += basePrice.low + severityModifier.low;
      totalHigh += basePrice.high + severityModifier.high;
    });
  }
  
  // Calculate regular services at base price
  regularSelected.forEach(serviceValue => {
    const basePrice = PLUMBING_SERVICE_PRICES[serviceValue] || { low: 0, high: 0 };
    totalLow += basePrice.low;
    totalHigh += basePrice.high;
  });
  
  // Apply access difficulty modifier to total
  const accessDifficulty = answers.access_difficulty || "easy";
  const accessModifier = ACCESS_DIFFICULTY_MODIFIERS[accessDifficulty] || { low: 0, high: 0 };
  
  // Only apply access modifier once, not per service
  totalLow += accessModifier.low;
  totalHigh += accessModifier.high;
  
  return {
    low: totalLow,
    high: totalHigh,
    hasValidPrice: totalLow > 0 || totalHigh > 0
  };
};

export default calculatePlumbingEstimate;
